import { Component, ChangeDetectionStrategy, input, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { UserDTO, UserService, PermissionDTO } from '../../services/user.service';
import { RoleService, RoleDTO } from '../../services/role.service';
import { UserType } from '../../core/models';

@Component({
  selector: 'app-user-read',
  templateUrl: './user-read.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule]
})
export class UserReadComponent {
  user = input.required<UserDTO>();

  private userService = inject(UserService);
  private roleService = inject(RoleService);

  roles = signal<RoleDTO[]>([]);
  rolesLoading = signal(false);

  permissions = signal<PermissionDTO[]>([]);
  permissionsLoading = signal(false);

  readonly UserType = UserType;

  constructor() {
    effect(() => {
      const userId = this.user()?.id;
      if (userId != null) {
        this.loadRoles(userId);
        this.loadPermissions(userId);
      } else {
        this.roles.set([]);
        this.permissions.set([]);
      }
    });
  }

  getDisplayName(user: UserDTO): string {
    if (!user) return '';
    const parts = [user.name, user.lastname, user.surname].filter(Boolean);
    return parts.length ? parts.join(' ').trim() : user.email || '-';
  }

  getTypeLabel(type: string | undefined): string {
    if (type === UserType.OWNER) return 'Propietario';
    if (type === UserType.USER) return 'Usuario';
    return type || '-';
  }

  permissionsByContext(): Array<{ context: string; label: string; permissions: PermissionDTO[] }> {
    const list = this.permissions();
    const map = new Map<string, PermissionDTO[]>();
    for (const p of list) {
      const context = p.value.includes(':') ? p.value.split(':')[0] : 'otros';
      if (!map.has(context)) map.set(context, []);
      map.get(context)!.push(p);
    }
    return Array.from(map.entries()).map(([context, permissions]) => ({
      context,
      label: context.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      permissions
    }));
  }

  private loadRoles(userId: number): void {
    this.rolesLoading.set(true);
    this.roleService
      .getUserRoles(userId)
      .pipe(
        catchError(() => of({ ok: false, body: [] as RoleDTO[] })),
        finalize(() => this.rolesLoading.set(false))
      )
      .subscribe((res) => {
        this.roles.set(res.ok ? res.body ?? [] : []);
      });
  }

  private loadPermissions(userId: number): void {
    this.permissionsLoading.set(true);
    this.userService
      .getUserPermissions(userId)
      .pipe(
        catchError(() => of({ ok: false, body: [] as PermissionDTO[] })),
        finalize(() => this.permissionsLoading.set(false))
      )
      .subscribe((res) => {
        this.permissions.set(res.ok ? res.body ?? [] : []);
      });
  }
}
